import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../_model/api-response';
import {
    AccountReactivationConfirmationDto,
    AccountReactivationRequestDto,
    AccountReactivationRequestResponseDto,
    AddSupportTicketMessageRequest,
    CreateSupportTicketRequest,
    PublicFaqItemDto,
    SupportChatRequest,
    SupportChatResponse,
    SupportDashboardSummaryDto,
    SupportMessageDto,
    SupportPagedResult,
    SupportTicketCategory,
    SupportTicketDetailsDto,
    SupportTicketListItemDto,
    SupportTicketPriority,
    SupportTicketStatus
} from '../../_model/support/support';

@Injectable({
    providedIn: 'root'
})
export class SupportService {
    private readonly baseUrl = `${environment.apiBaseUrl}/support`;
    private pageSize = environment.pageSize;

    constructor(private http: HttpClient) { }

    getFaqs(): Observable<ApiResponse<PublicFaqItemDto[]>> {
        return this.http.get<ApiResponse<PublicFaqItemDto[]>>(
            `${this.baseUrl}/faqs`
        );
    }

    chatPublic(payload: SupportChatRequest): Observable<ApiResponse<SupportChatResponse>> {
        return this.http.post<ApiResponse<SupportChatResponse>>(
            `${this.baseUrl}/chat/public`,
            payload
        );
    }

    chatAuthenticated(payload: SupportChatRequest): Observable<ApiResponse<SupportChatResponse>> {
        return this.http.post<ApiResponse<SupportChatResponse>>(
            `${this.baseUrl}/chat`,
            payload
        );
    }

    requestAccountReactivation(
        payload: AccountReactivationRequestDto
    ): Observable<ApiResponse<AccountReactivationRequestResponseDto>> {
        return this.http.post<ApiResponse<AccountReactivationRequestResponseDto>>(
            `${this.baseUrl}/account-reactivation/request`,
            payload
        );
    }

    confirmAccountReactivation(
        token: string
    ): Observable<ApiResponse<AccountReactivationConfirmationDto>> {
        return this.http.post<ApiResponse<AccountReactivationConfirmationDto>>(
            `${this.baseUrl}/account-reactivation/confirm`,
            { token }
        );
    }
    getMyTickets(page = 1, size?: number): Observable<ApiResponse<SupportTicketListItemDto[]>> {
        let params = new HttpParams().set('page', page);
        params = params.set('size', size ?? this.pageSize);

        return this.http.get<ApiResponse<SupportTicketListItemDto[]>>(
            `${this.baseUrl}/tickets`,
            { params }
        );
    }

    getMyTicket(ticketId: string): Observable<ApiResponse<SupportTicketDetailsDto>> {
        return this.http.get<ApiResponse<SupportTicketDetailsDto>>(
            `${this.baseUrl}/tickets/${ticketId}`
        );
    }

    createTicket(payload: CreateSupportTicketRequest): Observable<ApiResponse<SupportTicketDetailsDto>> {
        return this.http.post<ApiResponse<SupportTicketDetailsDto>>(
            `${this.baseUrl}/tickets`,
            payload
        );
    }

    addTicketMessage(
        ticketId: string,
        payload: AddSupportTicketMessageRequest
    ): Observable<ApiResponse<SupportMessageDto>> {
        return this.http.post<ApiResponse<SupportMessageDto>>(
            `${this.baseUrl}/tickets/${ticketId}/messages`,
            payload
        );
    }

    createTicketFromChat(
        subject: string,
        initialMessage: string,
        chat: SupportChatResponse
    ): Observable<ApiResponse<SupportTicketDetailsDto>> {
        return this.createTicket({
            subject,
            initialMessage,
            category: chat.suggestedCategory || 'General',
            relatedEntityType: chat.relatedEntityType || 'None',
            relatedEntityId: chat.relatedEntityId || null,
            contextSnapshotJson: chat.contextSnapshotJson || null,
            source: 'chatbot'
        });
    }

    getAdminTickets(
        page = 1,
        size?: number,
        search?: string,
        status?: SupportTicketStatus | null,
        category?: SupportTicketCategory | null,
        priority?: SupportTicketPriority | null
    ): Observable<ApiResponse<SupportTicketListItemDto[]>> {
        let params = new HttpParams()
            .set('page', page)
            .set('size', size ?? this.pageSize);

        if (search?.trim()) params = params.set('search', search.trim());
        if (status) params = params.set('status', status);
        if (category) params = params.set('category', category);
        if (priority) params = params.set('priority', priority);

        return this.http.get<ApiResponse<SupportTicketListItemDto[]>>(
            `${environment.apiBaseUrl}/admin/support/tickets`,
            { params }
        );
    }

    getAdminTicket(ticketId: string): Observable<ApiResponse<SupportTicketDetailsDto>> {
        return this.http.get<ApiResponse<SupportTicketDetailsDto>>(
            `${environment.apiBaseUrl}/admin/support/tickets/${ticketId}`
        );
    }

    addAdminTicketMessage(
        ticketId: string,
        message: string,
        isInternalNote = false
    ): Observable<ApiResponse<SupportMessageDto>> {
        const params = new HttpParams().set('isInternalNote', isInternalNote);

        return this.http.post<ApiResponse<SupportMessageDto>>(
            `${environment.apiBaseUrl}/admin/support/tickets/${ticketId}/messages`,
            { message },
            { params }
        );
    }

    reactivateAccountFromTicket(
        ticketId: string,
        reason?: string | null
    ): Observable<ApiResponse<SupportTicketDetailsDto>> {
        return this.http.post<ApiResponse<SupportTicketDetailsDto>>(
            `${environment.apiBaseUrl}/admin/support/tickets/${ticketId}/reactivate-account`,
            { reason: reason?.trim() || null }
        );
    }

    updateAdminTicketStatus(
        ticketId: string,
        status: SupportTicketStatus
    ): Observable<ApiResponse<SupportTicketDetailsDto>> {
        return this.http.post<ApiResponse<SupportTicketDetailsDto>>(
            `${environment.apiBaseUrl}/admin/support/tickets/${ticketId}/status`,
            { status }
        );
    }

    getAdminSupportSummary(): Observable<ApiResponse<SupportDashboardSummaryDto>> {
        return this.http.get<ApiResponse<SupportDashboardSummaryDto>>(
            `${environment.apiBaseUrl}/admin/support/summary`
        );
    }

    closeMyTicket(ticketId: string): Observable<ApiResponse<SupportTicketDetailsDto>> {
        return this.http.post<ApiResponse<SupportTicketDetailsDto>>(
            `${this.baseUrl}/tickets/${ticketId}/close`,
            {}
        );
    }

    reopenMyTicket(ticketId: string): Observable<ApiResponse<SupportTicketDetailsDto>> {
        return this.http.post<ApiResponse<SupportTicketDetailsDto>>(
            `${this.baseUrl}/tickets/${ticketId}/reopen`,
            {}
        );
    }
}